import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQuery } from '@mint-lib/query-client';
import { useFlags, useService } from '@mint-lib/routing-context';
import { GoogleAnalyticsIntegrations } from '@mint-sdk/api-integrations';

import { externalNavigateTo } from '../../utils/externalNavigation.js';

const getIntegrations = async () => {
  const { data } =
    await GoogleAnalyticsIntegrations.getGoogleAnalyticsIntegrations({});

  return data;
};

export const useGetGAIntegrations = ({ enabled }: { enabled: boolean }) => {
  const ff = useFlags('@mint/arm', ['integrationsQuerySdk']);
  const isIntegrationSdkEnabled = ff?.integrationsQuerySdk?.enabled;

  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['google_analytics'],
    queryFn: getIntegrations,
    enabled: enabled && !isIntegrationSdkEnabled,
  });

  const {
    data: googleAnalyticsIntegrations,
    isLoading: googleAnalyticsLoading,
  } = useQuery(
    {
      ...GoogleAnalyticsIntegrations.getQueryOptions({}),
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      enabled: enabled && isIntegrationSdkEnabled,
    },
    queryClient,
  );

  if (isIntegrationSdkEnabled) {
    return {
      data: googleAnalyticsIntegrations,
      isLoading: googleAnalyticsLoading,
    };
  }

  return {
    data: data,
    isLoading,
  };
};

export const useGA4IntegrationOauthUpdate = ({
  integrationId,
  platform,
}: {
  integrationId: string;
  platform: string;
}) => {
  const { t } = useTranslation('@myn/integrations_v2');
  const [queryClient] = useService({
    name: 'queryClient',
    suspense: true,
  });
  const [shouldReconnectWithGoogle, setShouldReconnectWithGoogle] =
    useState(false);

  const { data, isError, error } = useQuery(
    {
      ...GoogleAnalyticsIntegrations.getOauthQueryUpdateOptions({
        urlParams: { integrationId: integrationId || '' },
      }),
      enabled: shouldReconnectWithGoogle,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
    queryClient,
  );

  useEffect(() => {
    if (shouldReconnectWithGoogle && data?.[0]?.url) {
      setShouldReconnectWithGoogle(false);
      externalNavigateTo({ href: data[0].url });
    }
  }, [shouldReconnectWithGoogle, data]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar({
        removable: true,
        variant: 'error',
        title: t('Error'),
        subTitle:
          error.message ??
          t(
            'Something went wrong during account connection. Please try again or contact the support.',
          ),
      });
    }
  }, [isError, error]);

  const reconnectWithGoogle = useCallback(() => {
    if (integrationId && platform) {
      setShouldReconnectWithGoogle(true);
    }
  }, [integrationId, platform]);

  return { reconnectWithGoogle };
};
